/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import lodash from 'lodash';
import { Container, Button, Label, SVGIcon, FlexItem, FlexLayout } from '@waca/ca-ui-toolkit';
import addNew16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/add--filled_16.js';
import dismiss16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/close_16.js';
import stringResource from 'ba-filter-nls';
import ConditionTypeOptions from './ConditionTypeOptions';
import ConditionSelector from './ConditionSelector';
import OperatorSelector from './OperatorSelector';

export default class PredicateSelector extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		onConditionChange: PropTypes.func.isRequired,
		condition: PropTypes.object,
		onClose: PropTypes.func.isRequired,
		open: PropTypes.bool.isRequired,
	};

	state = {
		firstPredicate: this.props.condition?.predicates?.[0],
		secondPredicate: this.props.condition?.predicates?.[1],
		logicalOperation: this.props.condition?.logicalOperation,
		disableDoneButton: true
	 }

	static getPredicate(predicate) {
		return lodash.pick(predicate, ['conditionType', 'value', 'invert']);
	}

	componentWillReceiveProps(nextProps) {
		const { condition } = nextProps;

		this._setState(condition);
	}

	focus() {
		// Explicitly focus the 'clear all' button using the raw DOM API
		this._clearAllButton.focus();
	}

	_clearAllButton: Button;

	_setState = condition => {
		if (condition) {
			if (condition.predicates && condition.predicates.length > 1) {
				this.setState(
					{
						firstPredicate: condition.predicates[0],
						secondPredicate: condition.predicates[1],
						logicalOperation: condition.logicalOperation,
					},
					this._setDisableDoneButton
				);
			} else if (condition.predicates && condition.predicates.length === 1) {
				this.setState(
					{ firstPredicate: condition.predicates[0] },
					this._setDisableDoneButton
				);
			}
		} else {
			this.setState(
				{
					firstPredicate: undefined,
					secondPredicate: undefined,
					logicalOperation: undefined,
				},
				this._setDisableDoneButton
			);
		}
	};

	_setDisableDoneButton = () => {
		const { firstPredicate, secondPredicate } = this.state;

		if (firstPredicate && !secondPredicate) {
			this.setState({ disableDoneButton: false });
		} else if (firstPredicate && secondPredicate) {
			if (firstPredicate.value.length === 0 && secondPredicate.value.length !== 0) {
				this.setState({ disableDoneButton: true });
			} else {
				this.setState({ disableDoneButton: false });
			}
		} else {
			this.setState({ disableDoneButton: false });
		}
	};

	_onConditionChange = v => {
		if (v.position === 0) {
			this.setState({ firstPredicate: v.predicate }, this._setDisableDoneButton);
		} else {
			this.setState({ secondPredicate: v.predicate }, this._setDisableDoneButton);
		}
	};

	// A second condition has been added. A maximum of 2 conditions are allowed
	_onAddCondition = () => {
		const { defaultOption } = new ConditionTypeOptions();

		this.setState({
			secondPredicate: {
				conditionType: defaultOption.operation,
				value: '',
				invert: defaultOption.invert,
			},
			logicalOperation: 'and',
		});
	};

	_onOperatorChange = v => {
		this.setState({ logicalOperation: v, disableDoneButton: false });
	};

	_onClear = () => {
		this._setState(null);
	};

	_onDone = () => {
		const { onConditionChange } = this.props;
		const { firstPredicate, secondPredicate, logicalOperation } = this.state;
		let condition = null;

		if (firstPredicate && firstPredicate.value.length > 0) {
			condition = this._getDefaultCondition();
			condition.predicates = [];
			condition.predicates[0] = PredicateSelector.getPredicate(firstPredicate);

			if (secondPredicate && secondPredicate.value.length > 0) {
				condition.predicates[1] = PredicateSelector.getPredicate(secondPredicate);
				condition.logicalOperation = logicalOperation;
			}
		}
		if (condition || this.props.condition) {
			onConditionChange(condition);
		}
	};

	_getDefaultCondition = () => {
		const condition = {
			predicates: [
				{
					conditionType: 'equals',
					value: '',
					invert: false,
				},
			],
		};

		return condition;
	};

	render() {
		const { onClose, open } = this.props;
		const { firstPredicate, secondPredicate, logicalOperation, disableDoneButton } = this.state;

		// Hack alert!! There is an issue in IE11 when using SVG graphics. See RTC 190280
		// The solution is to always update the DOM. To do this, when the flyout is closed the iconId property is reset.
		// When the flyout is opened the correct id is set resulting in the SVGIcon component being re-rendered and
		// because the property value has changed the DOM updated.
		const addIconId = open ? addNew16.id : '';
		const closeIconId = open ? dismiss16.id : '';
		const addIcon = <SVGIcon iconId={addIconId} size="small" />;
		const closeIcon = <SVGIcon iconId={closeIconId} size="small" />;

		const closeString = stringResource.get('close');
		const addConditionString = stringResource.get('addCondition');
		const clearConditionsString = stringResource.get('clearConditions');
		const applyString = stringResource.get('apply');

		return (
			<Container role="application" gutter={[0, 1.25, 0, 1.25]} minWidth="400px">
				<FlexLayout direction="column">
					<FlexItem gutter={0}>
						<FlexLayout
							alignItems="center"
							direction="row"
							justifyContent="space-between"
						>
							<FlexItem textAlign="left" maxWidth="200px">
								<Label label={stringResource.get('setFilterCondition')} ellipsis />
							</FlexItem>
							<FlexItem>
								<Button
									data-tid="closeConditionClickable"
									onClick={onClose}
									intent="primary"
									variant="icon"
									icon={closeIcon}
									title={closeString}
									aria-label={closeString}
								/>
							</FlexItem>
						</FlexLayout>
					</FlexItem>
					<ConditionSelector
						data-tid="firstConditionSection"
						position={0}
						type={firstPredicate && firstPredicate.conditionType}
						invert={firstPredicate && firstPredicate.invert}
						value={firstPredicate ? firstPredicate.value : ''}
						onChange={this._onConditionChange}
					/>
					{!secondPredicate ? (
						<FlexItem gutter={[3, 0, 0, 0]} maxWidth="224px">
							<Button
								onClick={this._onAddCondition}
								label={addConditionString}
								title={addConditionString}
								aria-label={addConditionString}
								disabled={!firstPredicate || firstPredicate.value.length === 0}
								intent="primary"
								variant="inline"
								ellipsis
								icon={addIcon}
								data-tid="addConditionClickable"
							/>
						</FlexItem>
					) : (
						<FlexItem gutter={[2, 0, 0, 0]}>
							<OperatorSelector
								onChange={this._onOperatorChange}
								operator={logicalOperation}
							/>
							<ConditionSelector
								data-tid="secondConditionSection"
								position={1}
								type={secondPredicate && secondPredicate.conditionType}
								invert={secondPredicate && secondPredicate.invert}
								value={secondPredicate && secondPredicate.value}
								onChange={this._onConditionChange}
							/>
						</FlexItem>
					)}
					<FlexItem gutter={[3, 0, 0, 0]}>
						<FlexLayout
							alignItems="center"
							direction="row"
							justifyContent="space-between"
						>
							<FlexItem textAlign="left">
								<Button
									ref={btn => {
										this._clearAllButton = btn;
									}}
									data-tid="clearConditionsClickable"
									intent="primary"
									variant="inline"
									onClick={this._onClear}
									disabled={
										!firstPredicate ||
										(firstPredicate.value.length === 0 && !secondPredicate)
									}
									label={clearConditionsString}
									title={clearConditionsString}
									aria-label={clearConditionsString}
								/>
							</FlexItem>
							<FlexItem textAlign="right">
								<Button
									data-tid="applyClickable"
									intent="primary"
									onClick={this._onDone}
									disabled={disableDoneButton}
									label={applyString}
									title={applyString}
									aria-label={applyString}
								/>
							</FlexItem>
						</FlexLayout>
					</FlexItem>
				</FlexLayout>
			</Container>
		);
	}
}
